import React, { useState, useEffect } from 'react';
import { 
  Shield, 
  TrendingDown,
  Activity,
  Target,
  Zap,
  AlertCircle
} from 'lucide-react';
import { useApp } from '../../contexts/AppContext';
import { formatCurrency, formatPercent } from '../../utils/formatters';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend);

interface RiskMetrics {
  overallRiskScore: number;
  valueAtRisk: number;
  expectedShortfall: number;
  sharpeRatio: number;
  sortinoRatio: number;
  maxDrawdown: number;
  recoveryTime: number;
  betaCoefficient: number;
  stressTestResults: StressTestResult[];
  riskFactors: RiskFactor[];
  correlationMatrix: number[][];
}

interface StressTestResult {
  scenario: string;
  probability: number;
  impact: number;
  npvChange: number;
  irrChange: number;
  description: string;
}

interface RiskFactor {
  name: string;
  category: 'market' | 'credit' | 'operational' | 'liquidity';
  severity: 'low' | 'medium' | 'high' | 'critical';
  likelihood: number;
  impact: number;
  mitigation: string;
}

const RiskAnalytics: React.FC = () => {
  const { portfolio, currentInputs } = useApp();
  const [riskMetrics, setRiskMetrics] = useState<RiskMetrics | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [selectedView, setSelectedView] = useState<'overview' | 'stress' | 'factors' | 'portfolio'>('overview');
  const [confidenceLevel] = useState(95);

  useEffect(() => {
    if (portfolio.length > 0 || currentInputs) {
      calculateRiskMetrics();
    }
  }, [portfolio, currentInputs]);

  const calculateRiskMetrics = () => {
    setIsCalculating(true);
    
    setTimeout(() => {
      const metrics: RiskMetrics = {
        overallRiskScore: 65 + Math.random() * 20,
        valueAtRisk: -50000 - Math.random() * 100000,
        expectedShortfall: -75000 - Math.random() * 125000,
        sharpeRatio: 0.8 + Math.random() * 1.2,
        sortinoRatio: 1.0 + Math.random() * 1.5,
        maxDrawdown: -0.15 - Math.random() * 0.25,
        recoveryTime: 12 + Math.floor(Math.random() * 24),
        betaCoefficient: 0.7 + Math.random() * 0.6,
        stressTestResults: generateStressTests(),
        riskFactors: generateRiskFactors(),
        correlationMatrix: generateCorrelationMatrix()
      };
      
      setRiskMetrics(metrics);
      setIsCalculating(false);
    }, 1500);
  };

  const generateStressTests = (): StressTestResult[] => [
    {
      scenario: 'Zinssatzerhöhung +2%',
      probability: 35,
      impact: -15,
      npvChange: -25000,
      irrChange: -2.5,
      description: 'EZB erhöht Leitzins um 200 Basispunkte'
    },
    {
      scenario: 'Wirtschaftsrezession',
      probability: 20,
      impact: -25,
      npvChange: -45000,
      irrChange: -4.2,
      description: 'BIP-Rückgang > 2% für 2 Quartale'
    },
    {
      scenario: 'Immobilienblase platzt',
      probability: 10,
      impact: -35,
      npvChange: -75000,
      irrChange: -6.8,
      description: 'Immobilienpreise fallen um 30%'
    },
    {
      scenario: 'Mietmarkt-Kollaps',
      probability: 15,
      impact: -20,
      npvChange: -35000,
      irrChange: -3.5,
      description: 'Mieten fallen um 20%, Leerstand steigt'
    },
    {
      scenario: 'Regulatorische Änderungen',
      probability: 25,
      impact: -10,
      npvChange: -15000,
      irrChange: -1.8,
      description: 'Mietpreisbremse wird verschärft'
    }
  ];

  const generateRiskFactors = (): RiskFactor[] => [
    {
      name: 'Zinsänderungsrisiko',
      category: 'market',
      severity: 'high',
      likelihood: 70,
      impact: 80,
      mitigation: 'Langfristige Zinsbindung, Hedging-Strategien'
    },
    {
      name: 'Leerstandsrisiko',
      category: 'operational',
      severity: 'medium',
      likelihood: 40,
      impact: 60,
      mitigation: 'Diversifikation, attraktive Lagen wählen'
    },
    {
      name: 'Instandhaltungsrisiko',
      category: 'operational',
      severity: 'medium',
      likelihood: 60,
      impact: 50,
      mitigation: 'Rücklagen bilden, regelmäßige Inspektionen'
    },
    {
      name: 'Mietausfallrisiko',
      category: 'credit',
      severity: 'low',
      likelihood: 30,
      impact: 40,
      mitigation: 'Bonitätsprüfung, Mietkautionen'
    },
    {
      name: 'Liquiditätsrisiko',
      category: 'liquidity',
      severity: 'medium',
      likelihood: 35,
      impact: 70,
      mitigation: 'Liquiditätsreserve, flexible Finanzierung'
    },
    {
      name: 'Marktpreisrisiko',
      category: 'market',
      severity: 'high',
      likelihood: 50,
      impact: 85,
      mitigation: 'Langfristige Haltestrategie, Value-Investing'
    }
  ];

  const generateCorrelationMatrix = () => {
    const size = 5;
    const matrix: number[][] = [];
    for (let i = 0; i < size; i++) {
      matrix[i] = [];
      for (let j = 0; j < size; j++) {
        if (i === j) {
          matrix[i][j] = 1;
        } else if (j < i) {
          matrix[i][j] = matrix[j][i];
        } else {
          matrix[i][j] = (Math.random() - 0.5) * 0.8;
        }
      }
    }
    return matrix;
  };

  const getRiskScoreColor = (score: number) => {
    if (score < 40) return 'text-green-600 bg-green-50';
    if (score < 60) return 'text-yellow-600 bg-yellow-50';
    if (score < 80) return 'text-orange-600 bg-orange-50';
    return 'text-red-600 bg-red-50';
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'low': return 'bg-green-100 text-green-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'critical': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const renderOverview = () => {
    if (!riskMetrics) return null;

    return (
      <div className="space-y-6">
        {/* Risk Score Card */}
        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-gray-900">Gesamt-Risikobewertung</h3>
            <div className={`px-4 py-2 rounded-lg font-semibold ${getRiskScoreColor(riskMetrics.overallRiskScore)}`}>
              Risk Score: {riskMetrics.overallRiskScore.toFixed(0)}/100
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <TrendingDown className="w-8 h-8 mx-auto mb-2 text-red-600" />
              <div className="text-2xl font-bold text-gray-900">{formatCurrency(riskMetrics.valueAtRisk)}</div>
              <div className="text-sm text-gray-600">Value at Risk ({confidenceLevel}%)</div>
            </div>
            
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <Activity className="w-8 h-8 mx-auto mb-2 text-orange-600" />
              <div className="text-2xl font-bold text-gray-900">{formatCurrency(riskMetrics.expectedShortfall)}</div>
              <div className="text-sm text-gray-600">Expected Shortfall</div>
            </div>
            
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <Target className="w-8 h-8 mx-auto mb-2 text-blue-600" />
              <div className="text-2xl font-bold text-gray-900">{riskMetrics.sharpeRatio.toFixed(2)}</div>
              <div className="text-sm text-gray-600">Sharpe Ratio</div>
            </div>
            
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <Shield className="w-8 h-8 mx-auto mb-2 text-green-600" />
              <div className="text-2xl font-bold text-gray-900">{riskMetrics.sortinoRatio.toFixed(2)}</div>
              <div className="text-sm text-gray-600">Sortino Ratio</div>
            </div>
          </div>
        </div>

        {/* Risk Distribution */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg p-6 border border-gray-200">
            <h3 className="font-semibold text-gray-900 mb-4">Risikokategorien</h3>
            <Doughnut
              data={{
                labels: ['Marktrisiko', 'Kreditrisiko', 'Operationelles Risiko', 'Liquiditätsrisiko'],
                datasets: [{
                  data: [35, 20, 30, 15],
                  backgroundColor: [
                    'rgba(239, 68, 68, 0.8)',
                    'rgba(245, 158, 11, 0.8)',
                    'rgba(59, 130, 246, 0.8)',
                    'rgba(16, 185, 129, 0.8)'
                  ],
                  borderWidth: 0
                }]
              }}
              options={{
                plugins: {
                  legend: {
                    position: 'bottom'
                  }
                }
              }}
            />
          </div>

          <div className="bg-white rounded-lg p-6 border border-gray-200">
            <h3 className="font-semibold text-gray-900 mb-4">Drawdown-Analyse</h3>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">Max Drawdown</span>
                  <span className="font-semibold text-red-600">{formatPercent(riskMetrics.maxDrawdown)}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className="bg-red-600 h-3 rounded-full"
                    style={{ width: `${Math.abs(riskMetrics.maxDrawdown * 100)}%` }}
                  />
                </div>
              </div>
              
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">Recovery Time</span>
                  <span className="font-semibold">{riskMetrics.recoveryTime} Monate</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className="bg-blue-600 h-3 rounded-full"
                    style={{ width: `${Math.min(riskMetrics.recoveryTime / 36 * 100, 100)}%` }}
                  />
                </div>
              </div>
              
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">Beta Coefficient</span>
                  <span className="font-semibold">{riskMetrics.betaCoefficient.toFixed(2)}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className="bg-indigo-600 h-3 rounded-full"
                    style={{ width: `${Math.min(riskMetrics.betaCoefficient * 50, 100)}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderStressTests = () => {
    if (!riskMetrics) return null;

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <h3 className="font-semibold text-gray-900 mb-4">Stress-Test Szenarien</h3>
          
          <div className="space-y-4">
            {riskMetrics.stressTestResults.map((test, idx) => (
              <div key={idx} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="font-medium text-gray-900">{test.scenario}</h4>
                    <p className="text-sm text-gray-600 mt-1">{test.description}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    test.probability > 30 ? 'bg-red-100 text-red-800' :
                    test.probability > 20 ? 'bg-orange-100 text-orange-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {test.probability}% Wahrscheinlichkeit
                  </span>
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <span className="text-sm text-gray-600">Impact</span>
                    <div className="flex items-center mt-1">
                      <div className="w-24 bg-gray-200 rounded-full h-2 mr-2">
                        <div 
                          className="bg-red-600 h-2 rounded-full"
                          style={{ width: `${Math.abs(test.impact)}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-red-600">{test.impact}%</span>
                    </div>
                  </div>
                  
                  <div>
                    <span className="text-sm text-gray-600">NPV Änderung</span>
                    <div className="text-sm font-medium text-red-600 mt-1">
                      {formatCurrency(test.npvChange)}
                    </div>
                  </div>
                  
                  <div>
                    <span className="text-sm text-gray-600">IRR Änderung</span>
                    <div className="text-sm font-medium text-red-600 mt-1">
                      {test.irrChange.toFixed(1)}%
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Scenario Impact Chart */}
        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <h3 className="font-semibold text-gray-900 mb-4">Szenario-Impact-Analyse</h3>
          <Bar
            data={{
              labels: riskMetrics.stressTestResults.map(t => t.scenario),
              datasets: [
                {
                  label: 'NPV Impact (€)',
                  data: riskMetrics.stressTestResults.map(t => t.npvChange),
                  backgroundColor: 'rgba(239, 68, 68, 0.5)',
                  borderColor: 'rgb(239, 68, 68)',
                  borderWidth: 1,
                  yAxisID: 'y'
                },
                {
                  label: 'Wahrscheinlichkeit (%)',
                  data: riskMetrics.stressTestResults.map(t => t.probability),
                  backgroundColor: 'rgba(99, 102, 241, 0.5)',
                  borderColor: 'rgb(99, 102, 241)',
                  borderWidth: 1,
                  yAxisID: 'y1'
                }
              ]
            }}
            options={{
              responsive: true,
              scales: {
                y: {
                  type: 'linear',
                  display: true,
                  position: 'left',
                  title: {
                    display: true,
                    text: 'NPV Impact (€)'
                  }
                },
                y1: {
                  type: 'linear',
                  display: true,
                  position: 'right',
                  title: {
                    display: true,
                    text: 'Wahrscheinlichkeit (%)'
                  },
                  grid: {
                    drawOnChartArea: false
                  }
                }
              }
            }}
          />
        </div>
      </div>
    );
  };

  const renderRiskFactors = () => {
    if (!riskMetrics) return null;

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <h3 className="font-semibold text-gray-900 mb-4">Risikofaktoren-Matrix</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {riskMetrics.riskFactors.map((factor, idx) => (
              <div key={idx} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-medium text-gray-900">{factor.name}</h4>
                  <span className={`px-2 py-1 text-xs rounded-full ${getSeverityColor(factor.severity)}`}>
                    {factor.severity.toUpperCase()}
                  </span>
                </div>
                
                <div className="space-y-2 mb-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Eintrittswahrsch.</span>
                    <div className="flex items-center">
                      <div className="w-20 bg-gray-200 rounded-full h-2 mr-2">
                        <div 
                          className={`h-2 rounded-full ${
                            factor.likelihood > 60 ? 'bg-red-500' :
                            factor.likelihood > 40 ? 'bg-yellow-500' :
                            'bg-green-500'
                          }`}
                          style={{ width: `${factor.likelihood}%` }}
                        />
                      </div>
                      <span className="font-medium">{factor.likelihood}%</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Auswirkung</span>
                    <div className="flex items-center">
                      <div className="w-20 bg-gray-200 rounded-full h-2 mr-2">
                        <div 
                          className={`h-2 rounded-full ${
                            factor.impact > 70 ? 'bg-red-500' :
                            factor.impact > 50 ? 'bg-yellow-500' :
                            'bg-green-500'
                          }`}
                          style={{ width: `${factor.impact}%` }}
                        />
                      </div>
                      <span className="font-medium">{factor.impact}%</span>
                    </div>
                  </div>
                </div>
                
                <div className="border-t pt-2">
                  <p className="text-xs text-gray-600">
                    <span className="font-medium">Mitigation:</span> {factor.mitigation}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Risk Heat Map */}
        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <h3 className="font-semibold text-gray-900 mb-4">Risk Heat Map</h3>
          <div className="relative">
            <div className="grid grid-cols-5 gap-1">
              {[5, 4, 3, 2, 1].map(impact => (
                <React.Fragment key={impact}>
                  {[1, 2, 3, 4, 5].map(likelihood => {
                    const risk = impact * likelihood;
                    const factors = riskMetrics.riskFactors.filter(f => 
                      Math.floor(f.likelihood / 20) + 1 === likelihood &&
                      Math.floor(f.impact / 20) + 1 === impact
                    );
                    
                    return (
                      <div
                        key={`${impact}-${likelihood}`}
                        className={`aspect-square flex items-center justify-center text-xs font-medium rounded ${
                          risk >= 20 ? 'bg-red-500 text-white' :
                          risk >= 12 ? 'bg-orange-500 text-white' :
                          risk >= 6 ? 'bg-yellow-500 text-white' :
                          'bg-green-500 text-white'
                        }`}
                      >
                        {factors.length > 0 && factors.length}
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
            <div className="absolute -left-12 top-0 bottom-0 flex items-center">
              <span className="text-xs text-gray-600 -rotate-90">Impact →</span>
            </div>
            <div className="absolute left-0 right-0 -bottom-6 flex justify-center">
              <span className="text-xs text-gray-600">Likelihood →</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-500 to-orange-500 rounded-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-2 flex items-center">
              <Shield className="w-8 h-8 mr-3" />
              Erweiterte Risikoanalyse
            </h2>
            <p className="text-red-100">
              Portfolio VaR, Stress-Testing und Multi-Faktor-Risikomodelle
            </p>
          </div>
          <button
            onClick={calculateRiskMetrics}
            disabled={isCalculating}
            className="px-4 py-2 bg-white text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50 flex items-center"
          >
            {isCalculating ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-600 mr-2"></div>
                Berechnet...
              </>
            ) : (
              <>
                <Zap className="w-5 h-5 mr-2" />
                Neu berechnen
              </>
            )}
          </button>
        </div>
      </div>

      {/* Navigation */}
      <div className="bg-white rounded-lg p-4 border border-gray-200">
        <div className="flex space-x-2">
          {(['overview', 'stress', 'factors', 'portfolio'] as const).map(view => (
            <button
              key={view}
              onClick={() => setSelectedView(view)}
              className={`px-4 py-2 rounded ${
                selectedView === view
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {view === 'overview' && 'Übersicht'}
              {view === 'stress' && 'Stress-Tests'}
              {view === 'factors' && 'Risikofaktoren'}
              {view === 'portfolio' && 'Portfolio-Risiko'}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      {!riskMetrics && !isCalculating ? (
        <div className="bg-white rounded-lg p-12 border border-gray-200 text-center">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <p className="text-gray-600">
            Fügen Sie Immobilien zu Ihrem Portfolio hinzu oder geben Sie Daten im Analyzer ein, um die Risikoanalyse zu starten.
          </p>
        </div>
      ) : (
        <>
          {selectedView === 'overview' && renderOverview()}
          {selectedView === 'stress' && renderStressTests()}
          {selectedView === 'factors' && renderRiskFactors()}
          {selectedView === 'portfolio' && (
            <div className="bg-white rounded-lg p-6 border border-gray-200">
              <p className="text-gray-600 text-center">Portfolio-Risikoanalyse kommt bald...</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default RiskAnalytics;